import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useWorksheets } from '../hooks/useWorksheets';
import WorksheetList from '../components/worksheets/WorksheetList';
import CommonPageHeader from '../components/common/CommonPageHeader';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal';
import SearchBar from '../components/common/SearchBar';
import LoadingScreen from '../components/common/LoadingScreen';
import { Worksheet } from '../types/worksheet';
import { ClipboardDocumentListIcon, PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Worksheets: React.FC = () => {
  const { t } = useLanguage();
  const { loading, deleteWorksheet } = useWorksheets();
  const [shouldOpenForm, setShouldOpenForm] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [worksheetToDelete, setWorksheetToDelete] = useState<Worksheet | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleNewClick = () => {
    setShouldOpenForm(true);
  };

  const handleFormOpened = () => {
    setShouldOpenForm(false);
  };

  const handleDeleteWorksheet = async (worksheet: Worksheet) => {
    try {
      await deleteWorksheet(worksheet.id);
      toast.success(t('worksheets.messages.deleteSuccess'));
    } catch (error) {
      console.error('Error deleting worksheet:', error);
      toast.error(t('worksheets.messages.deleteError'));
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="space-y-4 w-full min-w-0">
      <CommonPageHeader
        title={t('worksheets.title')}
        subtitle={t('worksheets.subtitle')}
        icon={ClipboardDocumentListIcon}
      >
        <button
          onClick={handleNewClick}
          className="bg-white/20 backdrop-blur-sm text-white px-3 py-2 rounded-md text-sm flex items-center justify-center hover:bg-white/30 transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          <span>{t('worksheets.new')}</span>
        </button>
      </CommonPageHeader>

      {/* Search bar */}
      <div className="w-full">
        <SearchBar
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder={t('common.search')}
        />
      </div>

      <WorksheetList 
        triggerNewForm={shouldOpenForm}
        onFormOpened={handleFormOpened}
        onDeleteClick={(worksheet) => {
          setWorksheetToDelete(worksheet);
          setIsConfirmDeleteOpen(true);
        }}
        searchTerm={searchTerm}
      />

      {/* Confirm Delete Modal */}
      <ConfirmDeleteModal
        isOpen={isConfirmDeleteOpen}
        onClose={() => {
          setIsConfirmDeleteOpen(false);
          setWorksheetToDelete(null);
        }}
        onConfirm={() => {
          if (worksheetToDelete) {
            handleDeleteWorksheet(worksheetToDelete);
          }
          setIsConfirmDeleteOpen(false);
          setWorksheetToDelete(null);
        }}
        title={t('worksheets.messages.confirmTitle')}
        deleteMessage={worksheetToDelete ?
          `${t('worksheets.messages.deleteQuestion')} "${worksheetToDelete.title}"?` :
          t('worksheets.messages.deleteQuestion')}
        deleteWarning={t('worksheets.messages.deleteWarning')}
      />
    </div>
  );
};

export default Worksheets; 